import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Save, Sliders } from "lucide-react";

const SECTIONS = [
  { key: "show_categories_bento", label: "Categories bento", desc: "The 'Explore the shelf' grid on Home" },
  { key: "show_bestsellers", label: "Best sellers strip", desc: "'Best sellers this month' section on Home" },
  { key: "show_new_arrivals", label: "New arrivals strip", desc: "'Just landed' section on Home" },
  { key: "show_ritual", label: "Subscription ritual banner", desc: "Weekend Ritual / Subscribe & save block" },
  { key: "show_why_us", label: "Why coffee lovers choose us", desc: "3-feature promise section" },
  { key: "show_testimonials", label: "Testimonials", desc: "Customer reviews section on Home" },
  { key: "show_cta", label: "Final CTA banner", desc: "'Ready for a better morning?' CTA" },
  { key: "show_sort_dropdown", label: "Sort dropdown", desc: "Sort control on the Catalog page" },
  { key: "show_category_filters", label: "Category filter chips", desc: "Category pill filters on Catalog" },
  { key: "auto_hide_empty_categories", label: "Auto-hide empty categories", desc: "Categories with no active products disappear automatically" },
];

export default function AdminStorefrontSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/storefront/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const toggle = (k) => setSettings((s) => ({ ...s, [k]: !s[k] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/storefront/settings", settings);
      toast.success("Storefront layout saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (!settings) return <div className="text-stone-500">Loading…</div>;

  const visibleCount = SECTIONS.filter((s) => settings[s.key]).length;
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-outfit font-bold flex items-center gap-2">
            <Sliders className="w-6 h-6 text-[#D4AF37]" /> Storefront layout
          </h1>
          <div className="text-sm text-stone-500">
            Curate exactly what customers see. {visibleCount} / {SECTIONS.length} sections currently visible.
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-storefront-btn"
          className="bg-[#3B2F2F] text-white rounded-md px-5 py-2 flex items-center gap-2 disabled:bg-stone-400">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {SECTIONS.map((s) => (
          <div
            key={s.key}
            data-testid={`storefront-toggle-${s.key}`}
            onClick={() => toggle(s.key)}
            className={`cursor-pointer rounded-2xl border p-5 transition ${
              settings[s.key]
                ? "bg-[#FDFBF7] border-[#D4AF37]/40 shadow-sm"
                : "bg-stone-50 border-stone-200 opacity-70"
            }`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                settings[s.key] ? "bg-[#E8EDDF] text-[#2E4A35]" : "bg-stone-200 text-stone-500"
              }`}>
                {settings[s.key] ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <div className="font-outfit font-semibold text-[#3B2F2F]">{s.label}</div>
                <div className="text-xs text-stone-500 mt-1">{s.desc}</div>
              </div>
              <div className={`w-11 h-6 rounded-full flex items-center transition ${
                settings[s.key] ? "bg-[#2E4A35] justify-end" : "bg-stone-300 justify-start"
              } p-0.5`}>
                <span className="w-5 h-5 rounded-full bg-white shadow" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 text-xs text-stone-500 bg-[#FEF3E2] border border-[#D4AF37]/30 rounded-lg p-4">
        Changes go live the next time customers refresh the site. Hidden sections keep their data — nothing is deleted.
      </div>
    </div>
  );
}
